/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRiskGradient } from '../../../View/helper';
import PeriodWidget from '../../components/scl-components/period-widget';
import { Area } from '@antv/g2plot';
import moment from 'moment';
import styled from 'styled-components';
import {
  oneCase,
  middleCase,
  getMaxScore,
  getYAxis,
  getLineColor,
} from '../../../../components/dashboards/SupplyChainSummaryProject/components/riskWidget/helper';

const Style = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  overflow: hidden;
  .risk-history-tooltip {
    display: flex;
    flex-direction: column;
    padding: 12px 0px;
    gap: 8px;
    
    h1 {
      font-size: 14px;
      font-weight: 700;
      color: #001529;
    }

    .score {
      display: flex;
      gap: 10px;

      .cont {
        display: flex;
        flex-direction: column;
        justify-content: center;

        .label {
          margin-right: 12px;
        }

        label {
          margin-bottom: 0px;
          font-weight: 500;
          font-size: 12px;
          color: #001529;
        }

        .hexagons {
          display: flex;
          gap: 4px;
        }

        &.main-cont {
          flex: 1;
        }
      }
    }
  }
`;

const filterData = (data) => {
  let all = [];
  let maxScore = 0;

  Object.entries(data).forEach(([K, v]) => {
    all.push({ key: K, Score: data[K], date: K, value: K });
    if (data[K] > maxScore) {
      maxScore = data[K];
    }
  });

  if (all.length) {
    switch (all.length) {
      case 1:
        all = oneCase({ versions: all });
        break;
      case 2:
        all = middleCase({ versions: all, value: 8 });
        break;
      case 3:
        all = middleCase({ versions: all, value: 6 });
        break;
      case 4:
      case 5:
        all = middleCase({ versions: all, value: 5 });
        break;
      case 6:
      case 7:
        all = middleCase({ versions: all, value: 4 });
        break;
      default:
        all = middleCase({ versions: all, value: 2 });
    }
  }

  return { allData: all, maxScore };
};

export const hexagon = (active = false, fill = '') => `
  <svg width="12" height="12" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 4L6 7.4641L2 7.4641L-1.74846e-07 4L2 0.535898L6 0.535898L8 4Z" fill="${active ? fill : '#F4F4F4'}"/>
  </svg>
`;

const Tooltip = ({ data, t }) => {
  if (data[0]) {
    const score = Math.round(data[0].data.Score);
    const fill = getLineColor(score);

    return `
    <div class="risk-history-tooltip">
      <h1>${moment(data[0].title).format('DD MMMM YYYY')}</h1>
      <div class="score">
        <div class="cont main-cont">
          <label class="label">${t('Risk Score')}</label>
        </div>
        <div class="cont">
          <div class="hexagons">
          ${[5, 4, 3, 2, 1].map((i) => score >= i ? hexagon(true, fill) : hexagon(false)).join('')}
          </div>
        </div>
        <div class="cont">
          <label>
            ${Number(data[0].data.Score).toFixed(2)}
          </label>
        </div>
      </div>
    </div>
  `;
  }
  return '';
};

const RiskHistory = ({
  data: { data = {} },
  loading = false,
}) => {
  const { t } = useTranslation();
  const [mainGraph, setMainGraph] = useState(null);
  const container = useRef();
  const { allData, maxScore } = useMemo(() => filterData(data), [data]);

  useEffect(() => {
    let roundScore = getMaxScore('DEFAULT', maxScore);
    if (container.current) {
      if (mainGraph) {
        mainGraph.update({
          data: allData,
          areaStyle: () => {
            let fill = `l(270) ${getRiskGradient(roundScore)}`;
            return {
              fill,
            };
          },
          tooltip: {
            showTitle: false,
            customContent: (a, data) => {
              if (a && typeof a === 'string') {
                if (a.indexOf('_') === -1) {
                  return Tooltip({ data, t });
                }
              }
              return '';
            }
          },
        });
      } else {
        const area = new Area(container.current, {
          height: 300,
          data: allData,
          xField: 'date',
          localRefresh: true,
          yField: 'Score',
          point: {
            shape: 'round',
            size: 3,
            style: (a) => {
              const score = getMaxScore('DEFAULT', a.Score);
              const color = getLineColor(score);
              return {
                fill: (a.date.indexOf('_') !== -1) ? 'transparent' : 'white',
                stroke: (a.date.indexOf('_') !== -1) ? 'transparent' : color,
                lineWidth: 1,
              };
            },
          },
          xAxis: {
            label: {
              formatter: () => '',
            },
            nice: true,
          },
          yAxis: getYAxis(),
          tooltip: {
            showTitle: false,
            customContent: (a, data) => {
              if (a && typeof a === 'string') {
                if (a.indexOf('_') === -1) {
                  return Tooltip({ data, t });
                }
              }
              return '';
            }
          },
          areaStyle: () => {
            let fill = `l(270) ${getRiskGradient(roundScore)}`;
            return {
              fill,
            };
          },
          color: 'transparent',
        });
        area.render();
        setMainGraph(area);
      }
    }
  }, [container, allData]);

  return (
    <PeriodWidget
      loading={loading}
      title={t('Risk History')}
      className="custom-width-d with-border-header no-pt-body"
    >
      <Style>
        <div className="chart" style={{ height: 400 }} ref={container} />
      </Style>
    </PeriodWidget>
  );
};

export default RiskHistory;
